import React, { useMemo } from 'react';
import { Paper, Stack, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faRefresh } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../common/SearchableSelect';
import { useTranslation } from 'react-i18next';

const SalesFilter = ({
    salesStaff = [],
    salesStaffLoading = false,
    selectedStaffId,
    onStaffChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    status,
    onStatusChange,
    onSubmit,
    loading,
    activeTab,
    disableStaffSelector = false,
}) => {
    const { t } = useTranslation();
    const strings = t('salesAnalytics.filtersExtra', { returnObjects: true });

    const staffOptions = useMemo(() =>
        salesStaff.map((s) => ({
            value: s.id,
            label: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || s.email || String(s.id),
        })),
        [salesStaff]
    );

    const showStatus = activeTab === 'orders' || activeTab === 'deliveries';

    const statusOptions = activeTab === 'deliveries'
        ? [
            { value: '', label: strings.allStatuses },
            { value: 'SUPERVISOR_ASSIGNED', label: strings.statusOptions.assigned },
            { value: 'LOADING', label: strings.statusOptions.loading },
            { value: 'LOADED', label: strings.statusOptions.loaded },
            { value: 'IN_TRANSIT', label: strings.statusOptions.inTransit },
        ]
        : [
            { value: '', label: strings.allStatuses },
            { value: 'pending', label: strings.statusOptions.pending },
            { value: 'confirmed', label: strings.statusOptions.confirmed },
            { value: 'cancelled', label: strings.statusOptions.cancelled },
        ];

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3, mb: 3, borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.08)',
                bgcolor: '#FFFFFF',
            }}
        >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>

                {!disableStaffSelector && (
                    <div style={{ minWidth: 220 }}>
                        <SearchableSelect
                            options={staffOptions}
                            value={selectedStaffId}
                            onChange={(e) => onStaffChange(e.target.value)}
                            placeholder={strings.allSalesStaff}
                            disabled={salesStaffLoading}
                            loading={salesStaffLoading}
                        />
                    </div>
                )}

                <TextField
                    label={strings.fromDate}
                    type="date"
                    value={fromDate}
                    onChange={(e) => onFromDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 160, ...textFieldSx }}
                />
                <TextField
                    label={strings.toDate}
                    type="date"
                    value={toDate}
                    onChange={(e) => onToDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 160, ...textFieldSx }}
                />

                {showStatus && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{strings.status}</InputLabel>
                        <Select value={status} label={strings.status} onChange={(e) => onStatusChange(e.target.value)} sx={selectSx}>
                            {statusOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={loading || !fromDate || !toDate}
                    startIcon={<FontAwesomeIcon icon={loading ? faRefresh : faSearch} className={loading ? 'animate-spin' : ''} />}
                    sx={{
                        minWidth: 120, height: 40, borderRadius: 2,
                        textTransform: 'none', fontWeight: 600,
                        bgcolor: '#16a34a', boxShadow: 'none',
                        '&:hover': { bgcolor: '#15803d', boxShadow: '0 4px 12px rgba(21,128,61,0.3)' },
                    }}
                >
                    {loading ? strings.loading : strings.apply}
                </Button>
            </Stack>
        </Paper>
    );
};

const selectSx = {
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#86efac' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#16a34a' },
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        '&:hover fieldset': { borderColor: '#86efac' },
        '&.Mui-focused fieldset': { borderColor: '#16a34a' },
    },
};

export default SalesFilter;
